import React from 'react';
import {
  Camera, Sparkles, ListChecks, Shield, Award,
  Star, CheckCircle2, Zap, Clock, Smile, Target, ArrowDown, ChevronsDown, Bot, ScanEye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Reusable Card wrapper for clean styling
const LayerCard = ({ className = '', children, ...props }) => (
  <div
    className={`relative bg-white/90 border border-primary-50 rounded-3xl shadow-xl overflow-hidden ${className}`}
    {...props}
  >
    {children}
  </div>
);

const CTAButton = ({ label, onClick, variant = 'large' }) => {
  const sizeClasses = variant === 'small'
    ? 'text-lg px-6 py-3'
    : 'text-2xl px-8 py-4';
  return (
    <button
      onClick={onClick}
      className={`bg-yellow-500 text-white rounded-xl font-semibold
        hover:bg-yellow-600 shadow-lg hover:shadow-xl transition-all flex items-center gap-2 justify-center mx-auto
        ${sizeClasses}`}
      style={{ zIndex: 15 }}
    >
      {label}
    </button>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const goToScan = () => navigate('/scan');

  // Content Arrays
  const steps = [
    { title: 'Snap or Upload Your Smile', description: "Take a selfie or upload a photo—let's see your current smile with gaps.", icon: Camera },
    { title: 'AI Fills the Gaps', description: 'In seconds, see yourself with a complete, natural-looking smile.', icon: Sparkles },
    { title: 'Preview & Compare Instantly', description: 'See the before-and-after side-by-side. Download or share your new full smile.', icon: ListChecks }
  ];

  const features = [
    { title: 'Ultra-Realistic Gap Filling', description: 'See a true-to-life preview of your smile with gaps filled—no awkward editing or fake look.', icon: Sparkles, stats: 'Photorealistic AI' },
    { title: 'Instant & Effortless', description: 'Your smile transformation appears in seconds. No dentist visit needed.', icon: Zap, stats: 'Instant results' },
    { title: '100% Private & Secure', description: 'Your photos are never saved or shared. You’re always in control.', icon: Shield, stats: 'Totally Private' },
    { title: 'Built by dental students', description: 'Created by dental students with real knowledge of cosmetic dentistry and gap closure options.', icon: Award, stats: 'Expert-backed' }
  ];

  const testimonials = [
    { name: 'Amelia W.', role: 'Career Confidence', content: "Seeing myself with my gaps filled made me book my consultation. It’s the full smile I’ve always wanted for interviews!", image: 'https://randomuser.me/api/portraits/women/52.jpg' },
    { name: 'James P.', role: 'Social Transformation', content: "I didn’t realise how much difference closing the gaps would make until I saw this preview.", image: 'https://randomuser.me/api/portraits/men/47.jpg' },
    { name: 'Priya S.', role: 'Big Motivation', content: "My preview looked so natural—it finally gave me the push to fix my smile. I’m so excited for my real results!", image: 'https://randomuser.me/api/portraits/women/43.jpg' }
  ];

  const benefits = [
    { icon: Smile, title: 'Instant Confidence Boost', description: "See how a full smile without gaps could change your look—and your confidence." },
    { icon: Zap, title: 'No Dentist Visit Needed', description: 'Get your preview at home, on your own time. Totally free.' },
    { icon: Clock, title: 'Just 2 Minutes', description: 'No signup. No pressure. Your new smile is just a photo away.' }
  ];

  const stats = [
    { label: 'Smiles Visualized', value: '7K+' },
    { label: 'Photos Processed', value: '4K+' },
    { label: 'Dental Pros', value: '15+' },
    { label: 'Confidence Gained', value: '98%' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 via-white to-white relative">
      {/* HERO */}
      <section className="relative pt-12 pb-16 bg-gradient-to-b from-primary-50/70 to-white z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[340px] h-[340px] bg-yellow-200/30 rounded-full blur-3xl z-0" />
        <div className="container mx-auto max-w-5xl px-4 z-10 relative">
          <LayerCard className="p-8 md:p-12 text-center flex flex-col items-center">
            <div className="flex justify-center mb-4 mt-2">
              <div className="bg-white border-4 border-yellow-200 rounded-full p-2 shadow-md">
                <ScanEye size={30} className="text-yellow-400" />
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
              See Your<br/><span className="text-primary-500">Full Smile</span>.
            </h1>
            <ul className="space-y-2 mb-8">
              <li className="flex items-center justify-center text-l md:text-xl text-gray-700">
                <CheckCircle2 className="text-primary-500 mr-2" size={28} />
                Imagine Your Gaps Closed
              </li>
              <li className="flex items-center justify-center text-l md:text-xl text-gray-700">
                <CheckCircle2 className="text-primary-500 mr-2" size={28} />
                A Complete, Confident Smile
              </li>
              <li className="flex items-center justify-center text-l md:text-xl text-gray-700">
                <CheckCircle2 className="text-primary-500 mr-2" size={28} />
                Curious how you’ll look?
              </li>
            </ul>
            <CTAButton label="Scan Smile Now" onClick={goToScan} variant="large" />
            <p className="text-sm text-gray-500 mt-4">No login • 100% Free • 2 minutes</p>
          </LayerCard>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto max-w-5xl px-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefits.map((benefit, i) => (
            <LayerCard key={i} className="p-7 flex flex-col items-center text-center h-full">
              <div className="bg-primary-100 rounded-full p-3 mb-4">
                <benefit.icon size={32} className="text-primary-500" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
              <p className="text-gray-600 text-sm">{benefit.description}</p>
            </LayerCard>
          ))}
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="py-8 md:py-14 bg-gradient-to-r from-white to-primary-50/60">
        <div className="container mx-auto max-w-4xl px-4 flex flex-wrap justify-center gap-6">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white/90 border border-primary-50 rounded-2xl shadow-md px-7 py-4 flex flex-col items-center min-w-[130px]"
            >
              <span className="text-2xl font-bold text-primary-600">{stat.value}</span>
              <span className="text-sm text-gray-500">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-gradient-to-b from-primary-50/60 to-white">
        <div className="container mx-auto max-w-5xl px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {steps.map((step, i) => (
              <LayerCard key={i} className="px-6 pt-10 pb-7 flex flex-col items-center text-center">
                <div className="bg-yellow-100 border-2 border-yellow-200 rounded-full p-3 mb-2">
                  <step.icon size={36} className="text-yellow-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-primary-700">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </LayerCard>
            ))}
          </div>
          <div className="text-center mt-12">
            <CTAButton label="Try It For Free" onClick={goToScan} variant="small" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-white">
        <div className="container mx-auto max-w-5xl px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-14">
            Why Use ScanSmile?
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {features.map((feature, i) => (
              <LayerCard
                key={i}
                className="p-7 flex gap-5 items-center"
              >
                <div className="flex flex-col items-center min-w-[70px]">
                  <div className="w-12 h-12 bg-primary-100 shadow rounded-xl flex items-center justify-center mb-2">
                    <feature.icon size={26} className="text-primary-600" />
                  </div>
                  <div className="flex items-center gap-2 text-primary-600 font-medium mt-1 text-xs">
                    <CheckCircle2 size={16} />
                    {feature.stats}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1 text-primary-700">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </LayerCard>
            ))}
          </div>
          <div className="text-center mt-10">
            <CTAButton label="Preview Your Smile" onClick={goToScan} variant="small" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gradient-to-b from-white to-primary-50/40">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">
              Real People. Real Results.
            </h2>
            <p className="text-lg text-gray-600">
              See how others gained confidence with their instant full smile preview.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {testimonials.map((t, i) => (
              <LayerCard
                key={i}
                className="p-7 flex flex-col items-center text-center shadow-xl"
              >
                <img src={t.image} alt={t.name} className="w-16 h-16 rounded-full object-cover border-4 border-primary-50 shadow-lg mb-3" />
                <h4 className="font-semibold text-gray-900">{t.name}</h4>
                <p className="text-sm text-gray-500">{t.role}</p>
                <p className="text-gray-600 mt-3">{t.content}</p>
                <div className="mt-3 flex text-yellow-400">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} size={16} fill="currentColor" />
                  ))}
                </div>
              </LayerCard>
            ))}
          </div>
          <div className="text-center mt-14">
            <CTAButton label="Transform Your Smile Today" onClick={goToScan} variant="small" />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-yellow-100/40">
        <div className="container mx-auto max-w-4xl px-4">
          <LayerCard className="p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to See Your Smile Without Gaps?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              See your future smile—boost your confidence, photos, and first impressions.
            </p>
            <div className="flex flex-col items-center gap-4">
              <CTAButton label="See Smile Now" onClick={goToScan} variant="large" />
            </div>
          </LayerCard>
        </div>
      </section>
    </div>
  );
};

export default Home;
